import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CalendarPlus, ArrowLeft } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { SimpleLocationsSection } from '@/components/sections/simple-locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'introduction-section', title: 'Other Concerns' },
  { 
    id: 'musculoskeletal-pain', 
    title: 'Musculoskeletal & Structural Pain',
    children: [
      { id: 'headaches-migraines', title: 'Headaches & Migraines' },
      { id: 'cancer-treatment-support', title: 'Support During Cancer Treatment' },
    ]
  },
  { id: 'dont-see-situation', title: 'If You Don\'t See Your Situation Listed Here' },
  { 
    id: 'what-to-expect', 
    title: 'What to Expect',
    children: [
      { id: 'your-first-visit', title: 'Your First Visit' },
      { id: 'ongoing-care', title: 'Ongoing Care' },
    ]
  },
  { id: 'is-this-right', title: 'Is This Approach Right for You?' },
]

export default function OtherConcernsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const showBackLink = location.state?.fromParent === true
  const showHomepageBackLink = location.state?.fromHomepage === true
  const showAppointmentsBackLink = location.state?.fromAppointmentsPage === true

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Other Concerns | Raintree Chinese Medicine</title>
        <meta name="description" content="Other health concerns support at Raintree Chinese Medicine. Support for pain, headaches, migraines and stress-related symptoms." />
        <meta property="og:title" content="Other Concerns | Raintree Chinese Medicine" />
        <meta property="og:description" content="Other health concerns support at Raintree Chinese Medicine. Support for pain, headaches, migraines and stress-related symptoms." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with/other-concerns" />
      </Helmet>
      <h1 className="sr-only">Other Concerns</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0 relative">
        {showHomepageBackLink && (
          <button
            onClick={() => navigate('/#what-we-help-with-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        {showAppointmentsBackLink && !showHomepageBackLink && (
          <button
            onClick={() => navigate('/appointments#areas-of-focus-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to Appointments and Fees
          </button>
        )}
        {showBackLink && !showHomepageBackLink && !showAppointmentsBackLink && (
          <button
            onClick={() => navigate('/what-we-help-with')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Other Concerns
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Support for concerns that don't fit into standard categories.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('other_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('other_hero_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15min phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Other Concerns
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  While Raintree's main focus is women's health, fertility and hormones, some people also come for support with other concerns.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('other_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('other_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15min phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Other Concerns
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                While Raintree's main focus is women's health, fertility, pregnancy and hormonal care, some people also come for support with other concerns – particularly pain, headaches and the physical and emotional load of cancer treatment.
              </p>
              <p className="text-gray-600 text-left">
                This page outlines some of those areas and how acupuncture and Chinese medicine may be used as part of broader care.
              </p>
              <p className="text-gray-600 text-left mt-4">
                Treatments are always intended to sit <strong>alongside</strong>, not replace, the care you receive from your GP, specialist or oncology team.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Musculoskeletal & Structural Pain Section */}
      <section id="musculoskeletal-pain" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Musculoskeletal & Structural Pain
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Many people experience pain related to posture, work, caring responsibilities or long-term tension.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People may seek support for:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Neck, shoulder and upper back tension</li>
                <li>Lower back or hip discomfort</li>
                <li>Muscular pain related to overuse or repetitive tasks</li>
                <li>General tightness or aching that is worse with stress or long days</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Treatment in this area may include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Acupuncture to address local and related areas of tension</li>
                <li>Gentle techniques to support circulation and ease</li>
                <li>Considering how stress, sleep, hormones and workload may be contributing</li>
              </ul>
              <p className="text-gray-600 text-left">
                If you are already under care for a specific diagnosis (such as a spinal condition or injury), treatment can be planned with that in mind and does not replace medical or allied health care such as physiotherapy or exercise-based rehabilitation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Headaches & Migraines Section */}
      <section id="headaches-migraines" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Headaches & Migraines
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Headaches and migraines often sit at the intersection of hormones, stress, sleep and muscular tension.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People commonly come for help with:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Tension-type headaches, especially those that build up over the week</li>
                <li>Headaches linked to neck and shoulder tension</li>
                <li>Migraines that cluster around hormonal changes or the menstrual cycle</li>
                <li>Headaches that accompany stress, screen time or disrupted sleep</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Within this context, acupuncture and Chinese medicine may be used to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Look at timing and triggers over the month</li>
                <li>Work with muscular tension, stress and sleep patterns</li>
                <li>Offer a regular space for the nervous system to settle</li>
              </ul>
              <p className="text-gray-600 text-left">
                If you have red-flag symptoms or sudden changes in your headaches, these should always be checked with your GP or emergency services as appropriate.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Support During Cancer Treatment Section */}
      <section id="cancer-treatment-support" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Support During Cancer Treatment (Adjunctive Care)
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Some people seek acupuncture and Chinese medicine as part of their <strong>supportive care</strong> while undergoing or recovering from cancer treatment.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                This may include people who are:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Receiving chemotherapy, radiotherapy, immunotherapy or hormonal treatments</li>
                <li>Recovering after surgery</li>
                <li>Living with the longer-term effects of treatment</li>
                <li>In follow-up with their oncology team and wanting additional support for general wellbeing</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Common reasons people enquire about adjunctive care during cancer treatment include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Nausea, changes in appetite or digestive upset</li>
                <li>Fatigue and low energy</li>
                <li>Sleep disturbance</li>
                <li>Pain or discomfort related to treatment</li>
                <li>Feelings of anxiety, worry or emotional strain</li>
                <li>A wish to feel more grounded and supported during a difficult time</li>
              </ul>
              <p className="text-gray-600 text-left">
                In this setting, acupuncture is used <strong>only as supportive care</strong> – to help with comfort, stress and day-to-day wellbeing – and never as a replacement for oncology treatment.
              </p>
            </div>
            
            {/* Working with Your Oncology Team */}
            <div className="mt-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Working with Your Oncology Team
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  If you are currently receiving treatment for cancer, it is important that:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Your oncology team remains your primary source of medical advice and treatment</li>
                  <li>Any acupuncture or Chinese medicine you receive is discussed with, and considered alongside, their recommendations</li>
                  <li>You let your practitioners know about all medications and therapies you are using</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Renee will always encourage you to follow your oncology team's guidance and will refer you back to them, or to your GP, if something requires medical review.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* If You Don't See Your Situation Listed Here Section */}
      <section id="dont-see-situation" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              If You Don't See Your Situation Listed Here
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium text-base">
                Not every concern fits neatly into one category.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                You may be:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Living with a complex picture involving pain, hormones, stress and past illness</li>
                <li>Caring for others and noticing your own health sliding</li>
                <li>Simply feeling "not yourself" and unsure where to begin</li>
              </ul>
              <p className="text-gray-600 text-left">
                It is completely fine if you are not sure which area of focus describes you best. {' '}
                <button
                  onClick={() => openBooking('other_concerns_initial_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  The Initial Consultation Appointment
                </button>
                {' '}is designed to take in the whole picture and work out together whether this style of care is appropriate and how it might sit alongside your existing support.
              </p>
              <p className="text-gray-600 text-left mt-4">
                If you are still unsure, Renee offers a{' '}
                <button
                  onClick={() => openBooking('other_concerns_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to help clarify whether this style of care is likely to be helpful for you, or whether another type of support might be a better first step.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section id="what-to-expect" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect in an Appointment
            </h2>
            
            {/* Your First Visit */}
            <div id="your-first-visit" className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-xl mb-6 text-[#3f4141] text-left font-medium">
                  Your First Visit
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Your initial consultation and treatment is a longer appointment that allows time to:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Talk through your main concerns and how they affect daily life</li>
                  <li>Take a relevant health history, including current diagnoses, medications and treatments</li>
                  <li>Understand what other care you are already receiving (for example, from your GP, specialists or allied health practitioners)</li>
                  <li>Clarify your goals and what would feel most helpful right now</li>
                  <li>Begin acupuncture treatment, and, if appropriate, discuss Chinese herbal medicine and gentle, realistic self-care strategies</li>
                </ul>
                <p className="text-gray-600 text-left">
                  The focus is on understanding your situation clearly and agreeing on next steps that feel possible and safe.
                </p>
              </div>
            </div>

            {/* Ongoing Care */}
            <div id="ongoing-care">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Care
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Follow-up appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Checking in on how you have been physically and emotionally since the last visit</li>
                  <li>Adjusting treatment to how your body is responding, and to any changes in medical care</li>
                  <li>Reviewing how often it makes sense to continue, and when it may be appropriate to pause, reduce or increase support</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Plans are flexible and collaborative, always with an eye on what is sustainable and in alignment with your broader health care.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is This Approach Right for You? Section */}
      <section id="is-this-right" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Is This Approach Right for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Support in this "other concerns" area may be a good fit if you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Are looking for a gentle, adjunctive therapy alongside your existing care</li>
                <li>Want time and space to be listened to and treated as a whole person</li>
                <li>Are comfortable with a gradual, supportive approach rather than quick-fix promises</li>
                <li>Value care that works with your GP, specialists and oncology or allied health team, not instead of them</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                If you are unsure whether acupuncture and Chinese medicine are appropriate for your situation, it can be helpful to ask both your medical team and Renee questions before starting.
              </p>
              <p className="text-gray-600 text-left">
                You're welcome to book a{' '}
                <button
                  onClick={() => openBooking('other_approach_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to discuss your circumstances and clarify whether this is a suitable next step.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointments, Locations & How to Start */}
      <SimpleLocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `other_locations_${locationName.toLowerCase()}` })
        openBooking(`other_locations_${locationName.toLowerCase()}`)
      }} />

      {/* Next Steps CTA Section */}
      <section className="py-16 lg:py-32 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h2 className="text-2xl md:text-xl mb-10 text-[#3f4141]">
                Next steps
              </h2>
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  For new patients, the best starting point is:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 inline-block text-left max-w-lg mx-auto">
                  <li>
                    <strong>Initial Consultation & Treatment</strong> – a longer first appointment to gather a thorough picture and begin treatment if appropriate.
                  </li>
                </ul>
                <p className="text-gray-600 max-w-lg mx-auto">
                  If you are undergoing cancer treatment please mention this when booking so timing and safety can be considered carefully in line with your oncology care.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => {
                    trackEvent('book_appointment_click', { location: 'other_cta_view_appointments' })
                    navigate('/appointments')
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => openBooking('other_cta_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-gray-600 text-gray-600 hover:bg-gray-50 sm:w-1/2 mx-auto"
                onClick={() => openBooking('other_cta_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Title centered above two columns */}
            <div className="hidden lg:block">
              <h2 className="text-3xl lg:text-4xl mb-16 text-[#3f4141] text-center">
                Next steps
              </h2>
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
                <div>
                  <p className="text-gray-600 mb-4 text-base lg:text-md">
                    For new patients, the best starting point is:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-base lg:text-md">
                    <li>
                      <strong>Initial Consultation & Treatment</strong> – a longer first appointment to gather a thorough picture and begin treatment if appropriate.
                    </li>
                  </ul>
                  <p className="text-gray-600 text-base lg:text-md">
                    If you are undergoing cancer treatment please mention this when booking so timing and safety can be considered carefully in line with your oncology care.
                  </p>
                </div>
                <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => {
                      trackEvent('book_appointment_click', { location: 'other_cta_view_appointments' })
                      navigate('/appointments')
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      Appointment Types & Fees
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Button>
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('other_cta_initial_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Initial Consult & Treatment
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openBooking('other_cta_free_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Free 15-minute phone consult
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

